import { useMemo } from 'react';
import type { ReactNode } from 'react';

export interface ReportBlockContainerProps {
  width?: 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
}

/**
 * ReportBlockContainer - Wrapper container for report blocks
 *
 * Provides consistent styling and layout with responsive grid behavior.
 * Uses a 6-column grid system on desktop with responsive breakpoints.
 *
 * Grid System:
 * - Mobile (base): 2 columns - all blocks full width
 * - Tablet (md): 4 columns - responsive sizing
 * - Desktop (lg): 6 columns - full grid control
 *
 * Width Mappings (on desktop 6-col grid):
 * - 'sm': 2/6 columns (allows 3 blocks per row)
 * - 'md': 3/6 columns (allows 2 blocks per row)
 * - 'lg': 4/6 columns (allows 2 blocks per row)
 * - 'xl': 6/6 columns (full width)
 *
 * @param {ReportBlockContainerProps} props - Component properties
 * @param {'sm' | 'md' | 'lg' | 'xl'} [props.width='lg'] - Block width configuration
 * @param {ReactNode} props.children - Block content to render
 * @returns {React.ReactElement} Block container component
 *
 * @example
 * <ReportBlockContainer width="md">
 *   <CardHeader>...</CardHeader>
 *   <CardContent>...</CardContent>
 * </ReportBlockContainer>
 */
export const ReportBlockContainer = ({ width = 'xl', children }: ReportBlockContainerProps) => {
  const widthClass = useMemo(() => {
    switch (width) {
      case 'sm':
        return 'col-span-2 md:col-span-4 lg:col-span-2';
      case 'md':
        return 'col-span-2 md:col-span-4 lg:col-span-3';
      case 'lg':
        return 'col-span-2 md:col-span-4 lg:col-span-4';
      case 'xl':
        return 'col-span-2 md:col-span-4 lg:col-span-6';
      default:
        return 'col-span-2 md:col-span-4 lg:col-span-6';
    }
  }, [width]);

  return (
    <div className={`w-full overflow-hidden ${widthClass}`} data-testid="report-block-container">
      {children}
    </div>
  );
};
